import { useEffect, useRef, type ReactNode } from 'react';
import { CloseIcon } from './Icons';

/**
 * Native <dialog> as a modal: focus trap, Escape and inert background come from the browser.
 * Shows as a centered card on desktop and a bottom sheet on phones (see .modal-card in CSS).
 */
export function Modal({
  open,
  onClose,
  labelledBy,
  children,
}: {
  open: boolean;
  onClose: () => void;
  labelledBy: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="modal"
      aria-labelledby={labelledBy}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      // Click on the backdrop (the dialog element itself, outside the card) closes.
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {open && (
        <div className="modal-card">
          <span className="grabber" aria-hidden="true" />
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose} style={{ position: 'absolute', top: 12, right: 12 }}>
            <CloseIcon />
          </button>
          {children}
        </div>
      )}
    </dialog>
  );
}
